import styled from '@emotion/styled';
import classNames from 'classnames';
import Fuse from 'fuse.js';
import React from 'react';
import { useIntl } from 'react-intl';
import SelectSearch from 'react-select-search/dist/cjs';
// theme
import { fontSizes, theme } from '../../utils';
// components
import { Checkbox } from '../Checkbox';
import { Spinner } from '../Spinner';

function fuzzySearch(searchKeys?: string[]) {
  return (options) => {
    const fuse = new Fuse(options, {
      keys: searchKeys || ['name', 'groupName'],
      threshold: 0.3,
    });

    return (value: any) => {
      if (!value.length) {
        return options;
      }
      const formatedData = fuse.search(value).map((b: any) => {
        return b.item;
      });
      return formatedData;
    };
  };
}

const Label = styled.span`
  display: flex;
  align-items: baseline;
  font-size: ${fontSizes['xmedium']};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.palette.common.white};
`;

const Optional = styled.span`
  color: ${theme.palette.grey[400]};
  font-size: ${fontSizes['small']};
  margin-left: 5px;
  margin-right: 5px;
`;

const LabelLine = styled.span`
  display: flex;
  align-items: baseline;
  font-size: ${fontSizes['xmedium']};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.palette.common.white};
  margin-bottom: 6px;
`;

const OptionLine = styled.span`
  display: flex;
  align-items: center;
  justify-content: start;
`;

const ImgPremium = styled.img`
  margin-left: 10px;
  margin-top: -2px;
  width: 12px;
  height: 12px;
`;

const WrapperSelect = styled.div`
  position: relative;
`;
const WrapperLoader = styled.div`
  position: absolute;
  right: 30px;
  top: 11px;
`;

type SelectProps = {
  label?: string;
  disabled?: boolean;
  optional?: boolean;
  multiple?: boolean;
  isLoading?: boolean;
  onChange: any;
  name: string;
  options: Array<object>;
  placeholder?: string;
  searchable?: boolean;
  defaultValue?: string | Array<number>;
  searchKeys?: string[];
};

function renderValue(valueProps, snapshot, className) {
  const { option } = snapshot;
  const isMulti = Array.isArray(option);
  const inputVal = snapshot.focus && !isMulti ? snapshot.search : snapshot.displayValue;
  const classNameOverided = `${className} ${option?.premium && inputVal && 'bg-premium'}`;

  return <input {...valueProps} className={classNameOverided} value={inputVal} />;
}

function renderOption(props, option, snapshot, className, multiple) {
  return (
    <button {...props} className={className} type="button">
      <OptionLine>
        {multiple ? (
          <Checkbox
            name={`exceptional_asset_${option.value}`}
            checked={snapshot.selected}
            onChange={() => true}
          />
        ) : null}
        <span
          className={classNames({
            'text-grey-500': option.disabled,
            'text-gold': option.gold,
          })}
        >
          {option.name}
        </span>
        {option.meta ? (
          <span className="ml-1">{Object.keys(option.meta).map((key) => option.meta[key])}</span>
        ) : null}
        {option?.premium && <ImgPremium src="/img/premium.svg" alt="Finary premium only" />}
      </OptionLine>
    </button>
  );
}

export const Select: React.FC<SelectProps> = ({
  options,
  label,
  disabled,
  isLoading,
  onChange,
  name,
  optional,
  multiple,
  placeholder,
  searchable,
  defaultValue,
  searchKeys,
}) => {
  const i18n = useIntl();
  return (
    <div>
      {label || optional ? (
        <LabelLine>
          <Label>{label}</Label>
          {optional ? (
            <Optional>{i18n.formatMessage({ id: 'components.input.optional' })}</Optional>
          ) : null}
        </LabelLine>
      ) : null}
      <WrapperSelect>
        <SelectSearch
          className={!multiple ? 'select-search' : 'select-search select-search--multiple'}
          name={name}
          disabled={disabled}
          closeOnSelect={!multiple}
          printOptions={multiple ? 'on-focus' : 'auto'} // always, never, on-focus, auto
          multiple={multiple}
          emptyMessage={i18n.formatMessage({ id: 'components.input.not_found' })}
          placeholder={placeholder}
          onChange={onChange}
          options={options}
          value={defaultValue}
          filterOptions={fuzzySearch(searchKeys)}
          search={searchable ? true : false}
          renderOption={(props, option, snapshot, className) =>
            renderOption(props, option, snapshot, className, multiple)
          }
          renderValue={renderValue}
        />
        {isLoading ? (
          <WrapperLoader>
            <Spinner color="#F1C086" />
          </WrapperLoader>
        ) : null}
      </WrapperSelect>
      <style jsx global>
        {`
          .select-search {
            position: relative;
            box-sizing: border-box;
          }

          .select-search *,
          .select-search *::after,
          .select-search *::before {
            box-sizing: inherit;
          }

          /**
        * Value wrapper
        */
          .select-search__value {
            position: relative;
            overflow: hidden;
            z-index: 1;
          }

          .select-search__value::after {
            content: '';
            display: inline-block;
            position: absolute;
            top: calc(50% - 9px);
            right: 19px;
            width: 11px;
            height: 11px;
          }

          /**
        * Input
        */
          .select-search__input {
            display: block;
            height: 48px;
            width: 100%;
            padding: 0 16px;
            box-shadow: 0 0.0625rem 0.125rem rgba(0, 0, 0, 0.15);
            border-radius: 3px;
            outline: none;
            font-size: 14px;
            text-align: left;
            text-overflow: ellipsis;
            line-height: 36px;
            -webkit-appearance: none;
            border: 1px solid rgba(255, 255, 255, 0.32);
            background: rgba(82, 82, 82, 0.5);
            font-weight: 600;
            transition: all 0.2s ease-in-out;
          }

          .select-search__input::-webkit-search-decoration,
          .select-search__input::-webkit-search-cancel-button,
          .select-search__input::-webkit-search-results-button,
          .select-search__input::-webkit-search-results-decoration {
            -webkit-appearance: none;
          }

          .select-search__input:not([readonly]):focus {
            cursor: initial;
          }
          .select-search__input::placeholder {
            color: #525252;
          }
          /**
        * Options wrapper
        */
          .select-search__select {
            box-shadow: 0 0.0625rem 0.125rem rgba(0, 0, 0, 0.15);
          }

          /**
        * Options
        */
          .select-search__options {
            list-style: none;
          }

          /**
        * Option row
        */
          .select-search__row:not(:first-child) {
            // border-top: 1px solid #eee;
          }

          /**
        * Option
        */
          .select-search__option,
          .select-search__not-found {
            display: block;
            width: 100%;
            padding: 12px 16px;
            background: rgba(61, 61, 61, 1);
            color: #a3a3a3;
            border: none;
            outline: none;
            font-size: 12px;
            text-align: left;
            cursor: pointer;
            border: 1px solid rgba(41, 41, 41, 0);
            border-radius: 2px;
            margin: 2px 0;
            transition: all 0.2s ease-in-out;
          }

          .select-search--multiple .select-search__option {
            height: 48px;
          }

          .select-search__option.is-selected {
            background: #525252;
            color: #fff;
          }

          .select-search__option.is-highlighted,
          .select-search__option:not(.is-selected):hover {
            background: #525252;
            border: 1px solid rgba(41, 41, 41, 0.5);
          }

          .select-search__option.is-highlighted.is-selected,
          .select-search__option.is-selected:hover {
            background: #525252;
            color: #fff;
            border: 1px solid rgba(41, 41, 41, 0.5);
          }
          /**
         * States
         */
          .select-search.is-disabled {
            opacity: 0.5;
          }

          .select-search.is-loading .select-search__value::after {
            background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='50' height='50' viewBox='0 0 50 50'%3E%3Cpath fill='%232F2D37' d='M25,5A20.14,20.14,0,0,1,45,22.88a2.51,2.51,0,0,0,2.49,2.26h0A2.52,2.52,0,0,0,50,22.33a25.14,25.14,0,0,0-50,0,2.52,2.52,0,0,0,2.5,2.81h0A2.51,2.51,0,0,0,5,22.88,20.14,20.14,0,0,1,25,5Z'%3E%3CanimateTransform attributeName='transform' type='rotate' from='0 25 25' to='360 25 25' dur='0.6s' repeatCount='indefinite'/%3E%3C/path%3E%3C/svg%3E");
            background-size: 11px;
          }

          .select-search:not(.is-disabled) .select-search__input {
            cursor: pointer;
          }
          /**
         * Modifiers
         */
          // .select-search--multiple {
          //   border-radius: 3px;
          //   overflow: hidden;
          // }

          .select-search:not(.is-loading) .select-search__value::after {
            width: 0;
            height: 0;
            margin-top: 7px;
            border-left: 4px solid transparent;
            border-right: 4px solid transparent;

            border-top: 4px solid #fff;
          }
          .select-search .select-search__input:hover {
            border-color: #f1c086;
            background-color: rgba(241, 192, 134, 0.2);
          }
          .select-search.has-focus .select-search__value::after {
            transform: rotate(180deg);
          }
          .select-search .select-search__select {
            position: absolute;
            z-index: 2;
            top: 52px;
            border-radius: 2px;
            right: 0;
            left: 0;
            overflow: auto;
            max-height: 250px;
            background: #292929;
          }

          .select-search__not-found {
            text-align: center;
          }
          .bg-premium {
            background-image: url(/img/premium.svg);
            background-repeat: no-repeat;
            background-size: 10px;
            transition-property: none;
            background-position: center right 40px;
          }
        `}
      </style>
    </div>
  );
};

export default Select;
